"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/lib/components/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/lib/components/form";
import { Input } from "@/lib/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/components/select";
import { Textarea } from "@/lib/components/textarea";
import { useAppConfig } from "@/lib/config";
import { countries } from "@/lib/countries";
import { useI18n } from "@llmgateway/i18n/react";
import { CalendlyInline } from "./calendly-inline";
import { selectEnterpriseText } from "./i18n";
const CALENDLY_ENTERPRISE_URL = "https://calendly.com/llmgateway/llmgateway-enterprise";
const companySizeValues = ["1-50", "51-200", "201-500", "501-1000", "1000+"];
type ContactValidationMessages = ReturnType<typeof selectEnterpriseText>["contact"]["validation"];
function createContactFormSchema(messages: ContactValidationMessages) {
  return z.object({
    name: z.string().min(2, messages.name),
    email: z.string().email(messages.email),
    country: z.string().min(1, messages.country),
    size: z.string().min(1, messages.size),
    message: z.string().min(10, messages.message),
    honeypot: z.string().optional(),
    timestamp: z.number().optional()
  });
}
type ContactFormData = z.infer<ReturnType<typeof createContactFormSchema>>;
export function ContactFormEnterprise() {
  const {
    locale
  } = useI18n();
  const text = selectEnterpriseText(locale).contact;
  const config = useAppConfig();
  const posthog = usePostHog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [scheduled, setScheduled] = useState<{
    name: string;
    email: string;
  }>({
    name: "",
    email: ""
  });
  const [formLoadTime] = useState(() => Date.now());
  const contactFormSchema = useMemo(() => createContactFormSchema(text.validation), [text.validation]);
  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      country: "",
      size: "",
      message: "",
      honeypot: "",
      timestamp: formLoadTime
    }
  });

  // Update timestamp when form loads
  useEffect(() => {
    form.setValue("timestamp", formLoadTime);
  }, [form, formLoadTime]);
  const onSubmit = async (data: ContactFormData) => {
    posthog.capture("enterprise_contact_submitted", {
      country: data.country,
      companySize: data.size
    });
    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.apiUrl}/public/contact/enterprise`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });
      const result = (await response.json()) as {
        success: boolean;
        message?: string;
      };
      if (response.ok && result.success) {
        posthog.capture("enterprise_contact_success", {
          country: data.country,
          companySize: data.size
        });
        setScheduled({
          name: data.name,
          email: data.email
        });
        setIsSuccess(true);
        form.reset();
        toast.success(text.toast.successTitle, {
          description: text.toast.successDescription
        });
      } else {
        toast.error(text.toast.errorTitle, {
          description: result.message ?? text.toast.errorDescription
        });
      }
    } catch {
      toast.error(text.toast.unknownTitle, {
        description: text.toast.unknownDescription
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section className="py-20 sm:py-28" id="contact">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-4xl">
					<div className="text-center mb-12">
						<h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-balance">
							{text.title}
						</h2>
						<p className="text-lg text-muted-foreground text-balance leading-relaxed max-w-2xl mx-auto">
							{text.description}
						</p>
					</div>

					<div className="rounded-2xl border border-border bg-card/50 backdrop-blur-sm p-8 sm:p-10 shadow-lg">
						{isSuccess ? <div className="py-4">
								<div className="text-center">
									<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-6">
										<CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
									</div>
									<h3 className="text-2xl font-semibold mb-2">
										{text.successTitle}
									</h3>
									<p className="text-muted-foreground">
										{text.successDescription}
									</p>
								</div>

								<div className="mt-8">
									<CalendlyInline url={CALENDLY_ENTERPRISE_URL} name={scheduled.name} email={scheduled.email} />
								</div>

								<div className="mt-6 text-center">
									<Button onClick={() => setIsSuccess(false)} variant="outline" size="lg">
										{text.sendAnother}
									</Button>
								</div>
							</div> : <Form {...form}>
								<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
									{/* Honeypot field - hidden from users but visible to bots */}
									<FormField control={form.control} name="honeypot" render={({
                field
              }) => <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
												<FormItem>
													<FormLabel>{text.honeypotLabel}</FormLabel>
													<FormControl>
														<Input {...field} tabIndex={-1} autoComplete="off" />
													</FormControl>
												</FormItem>
											</div>} />

									<div className="grid gap-6 sm:grid-cols-2">
										<FormField control={form.control} name="name" render={({
                  field: field_0
                }) => <FormItem>
													<FormLabel>
														{text.fields.name}{" "}
														<span className="text-destructive">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder={text.placeholders.name} {...field_0} className="bg-background h-11" />
													</FormControl>
													<FormMessage />
												</FormItem>} />

										<FormField control={form.control} name="email" render={({
                  field: field_1
                }) => <FormItem>
													<FormLabel>
														{text.fields.email}{" "}
														<span className="text-destructive">*</span>
													</FormLabel>
													<FormControl>
														<Input type="email" placeholder={text.placeholders.email} {...field_1} className="bg-background h-11" />
													</FormControl>
													<FormMessage />
												</FormItem>} />
									</div>

									<div className="grid gap-6 sm:grid-cols-2">
										<FormField control={form.control} name="country" render={({
                  field: field_2
                }) => <FormItem>
													<FormLabel>
														{text.fields.country}{" "}
														<span className="text-destructive">*</span>
													</FormLabel>
													<Select onValueChange={field_2.onChange} defaultValue={field_2.value}>
														<FormControl>
															<SelectTrigger className="w-full bg-background h-11">
																<SelectValue placeholder={text.placeholders.country} />
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															{countries.map(country => <SelectItem key={country} value={country}>
																	{country}
																</SelectItem>)}
														</SelectContent>
													</Select>
													<FormMessage />
												</FormItem>} />

										<FormField control={form.control} name="size" render={({
                  field: field_3
                }) => <FormItem>
													<FormLabel>
														{text.fields.size}{" "}
														<span className="text-destructive">*</span>
													</FormLabel>
													<Select onValueChange={field_3.onChange} defaultValue={field_3.value}>
														<FormControl>
															<SelectTrigger className="w-full bg-background h-11">
																<SelectValue placeholder={text.placeholders.size} />
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															{companySizeValues.map((value, index) => <SelectItem key={value} value={value}>
																	{text.companySizes[index] ?? value}
																</SelectItem>)}
														</SelectContent>
													</Select>
													<FormMessage />
												</FormItem>} />
									</div>

									<FormField control={form.control} name="message" render={({
                field: field_4
              }) => <FormItem>
												<FormLabel>
													{text.fields.message}{" "}
													<span className="text-destructive">*</span>
												</FormLabel>
												<FormControl>
													<Textarea placeholder={text.placeholders.message} {...field_4} className="min-h-32 bg-background resize-none" />
												</FormControl>
												<FormMessage />
											</FormItem>} />

									<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:justify-between pt-2">
										<p className="text-sm text-muted-foreground">
											<span className="text-destructive">*</span>{" "}
											{text.requiredFields}
										</p>
										<Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-w-[180px]">
											{isSubmitting ? <>
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													{text.submitting}
												</> : <>
													{text.submit}
													<ArrowRight className="ml-2 h-4 w-4" />
												</>}
										</Button>
									</div>
								</form>
							</Form>}
					</div>
				</div>
			</div>
		</section>;
}